/**
 * „Gilt für": alle Kunden oder nur bestimmte — im Editor der Wissensdatenbank und im
 * Anleitungs-Entwurf von KIAN derselbe Baustein.
 *
 * Die Kundensuche läuft über den Kunden-Hub (für alle Techniker freigegeben) und liefert die
 * lokale Kunden-ID; gewählte Kunden bleiben in den Optionen, sonst verlöre das Feld ihre Namen,
 * sobald sich die Suchtreffer ändern.
 */
import { useMemo, useState } from 'react';
import { Badge, Group, MultiSelect, SegmentedControl, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

export type KbScope = 'ALL' | 'CUSTOMERS';

export interface KbCustomerRef {
  id: string;
  /** null = Kunde gibt es nicht mehr */
  name: string | null;
}

export interface KbCustomerScopeValue {
  scope: KbScope;
  customers: KbCustomerRef[];
}

function customerLabel(c: { name: string | null; customerNumber?: string | null }): string {
  const name = c.name ?? 'Unbekannter Kunde';
  return c.customerNumber ? `${name} (${c.customerNumber})` : name;
}

export function KbCustomerScopeField({
  value,
  onChange,
  disabled,
  error,
}: {
  value: KbCustomerScopeValue;
  onChange: (next: KbCustomerScopeValue) => void;
  disabled?: boolean;
  error?: string | null;
}) {
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const { data, isFetching } = useQuery({
    queryKey: ['kb-customer-scope-search', debounced],
    queryFn: () => api.searchHubCustomers(debounced, 1, 20),
    enabled: value.scope === 'CUSTOMERS' && debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  const found = useMemo(() => data?.customers ?? [], [data]);
  const options = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ value: string; label: string }> = [];
    for (const c of value.customers) {
      seen.add(c.id);
      out.push({ value: c.id, label: customerLabel(c) });
    }
    for (const c of found) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push({ value: c.id, label: customerLabel(c) });
    }
    return out;
  }, [found, value.customers]);

  const pick = (ids: string[]) => {
    const known = new Map<string, KbCustomerRef>(value.customers.map((c) => [c.id, c]));
    for (const c of found) if (!known.has(c.id)) known.set(c.id, { id: c.id, name: c.name });
    onChange({ scope: 'CUSTOMERS', customers: ids.map((id) => known.get(id) ?? { id, name: null }) });
  };

  return (
    <Stack gap={6}>
      <Text size="sm" fw={500}>
        Gilt für
      </Text>
      <SegmentedControl
        size="xs"
        disabled={disabled}
        value={value.scope}
        onChange={(v) => onChange({ scope: v as KbScope, customers: v === 'ALL' ? [] : value.customers })}
        data={[
          { value: 'ALL', label: 'Alle Kunden' },
          { value: 'CUSTOMERS', label: 'Bestimmte Kunden' },
        ]}
      />
      {value.scope === 'CUSTOMERS' ? (
        <MultiSelect
          placeholder={value.customers.length ? undefined : 'Kunden suchen …'}
          description="Die Lösung hängt an der Umgebung dieser Kunden (Server, Drucker, Software)."
          searchable
          clearable
          disabled={disabled}
          value={value.customers.map((c) => c.id)}
          onChange={pick}
          searchValue={search}
          onSearchChange={setSearch}
          data={options}
          filter={({ options: opts }) => opts}
          nothingFoundMessage={
            debounced.trim().length < 2 ? 'Mind. 2 Zeichen tippen' : isFetching ? 'Suche …' : 'Kein Kunde gefunden'
          }
          error={error ?? undefined}
          comboboxProps={{ zIndex: 5400 }}
        />
      ) : null}
    </Stack>
  );
}

/** Gültigkeit als Badges: „Alle Kunden" oder je Kunde eines — für Listen, Artikel und Freigabe. */
export function KbScopeBadges({ customers, size = 'sm' }: { customers: KbCustomerRef[]; size?: 'xs' | 'sm' }) {
  if (!customers.length) {
    return (
      <Badge size={size} variant="light" color="gray">
        Alle Kunden
      </Badge>
    );
  }
  return (
    <Group gap={4} wrap="wrap">
      <Text size="xs" c="dimmed">
        Nur für
      </Text>
      {customers.map((c) => (
        <Badge key={c.id} size={size} variant="light" color="blue">
          {c.name ?? 'Unbekannter Kunde'}
        </Badge>
      ))}
    </Group>
  );
}
